// Utility to load GeoJSON files for district boundaries
import { STATE_NAMES, getCandidatesTableName } from './stateMapping';
import { supabase } from './supabase';
import { getCaliforniaDistricts } from './californiaData';

/**
 * Convert Web Mercator (EPSG:3857) coordinates to WGS84 (EPSG:4326)
 * Leaflet expects coordinates in [longitude, latitude] format
 */
function webMercatorToWGS84(coordinates) {
  const EARTH_RADIUS = 6378137; // Earth's radius in meters

  function convertPoint(point) {
    const [x, y] = point;
    const lng = (x / EARTH_RADIUS) * (180 / Math.PI);
    const lat = (Math.atan(Math.exp(y / EARTH_RADIUS)) * 2 - Math.PI / 2) * (180 / Math.PI);
    return [lng, lat];
  }

  function processCoordinates(coords, depth) {
    if (depth === 0) {
      return convertPoint(coords);
    }
    return coords.map(c => processCoordinates(c, depth - 1));
  }

  return processCoordinates(coordinates, getCoordinateDepth(coordinates));
}

/**
 * Determine the nesting depth of coordinate arrays
 * - Point: depth 0
 * - LineString/MultiPoint: depth 1
 * - Polygon/MultiLineString: depth 2
 * - MultiPolygon: depth 3
 */
function getCoordinateDepth(coords) {
  if (!Array.isArray(coords)) return -1;
  if (!Array.isArray(coords[0])) return 0;
  if (!Array.isArray(coords[0][0])) return 1;
  if (!Array.isArray(coords[0][0][0])) return 2;
  return 3;
}

// Re-export for convenience
export const US_STATES = STATE_NAMES;

// Old export kept for backwards compatibility
const US_STATES_OLD = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming'
};

// Number of districts per state (2026 cycle)
export const DISTRICTS_PER_STATE = {
  'AL': 7, 'AK': 1, 'AZ': 9, 'AR': 4, 'CA': 52, 'CO': 8, 'CT': 5, 'DE': 1,
  'FL': 28, 'GA': 14, 'HI': 2, 'ID': 2, 'IL': 17, 'IN': 9, 'IA': 4, 'KS': 4,
  'KY': 6, 'LA': 6, 'ME': 2, 'MD': 8, 'MA': 9, 'MI': 13, 'MN': 8, 'MS': 4,
  'MO': 8, 'MT': 2, 'NE': 3, 'NV': 4, 'NH': 2, 'NJ': 12, 'NM': 3, 'NY': 26,
  'NC': 14, 'ND': 1, 'OH': 15, 'OK': 5, 'OR': 6, 'PA': 17, 'RI': 2, 'SC': 7,
  'SD': 1, 'TN': 9, 'TX': 38, 'UT': 4, 'VT': 1, 'VA': 11, 'WA': 10, 'WV': 2,
  'WI': 8, 'WY': 1
};

/**
 * Load all GeoJSON files for a specific state
 * @param {string} stateCode - Two-letter state code (e.g., 'CA', 'NY')
 * @returns {Promise<Array>} Array of loaded GeoJSON features
 */
export async function loadStateDistricts(stateCode) {
  if (!stateCode || !US_STATES[stateCode]) {
    console.error(`❌ Invalid state code: ${stateCode}`);
    throw new Error(`Invalid state code: ${stateCode}`);
  }

  const districtCount = DISTRICTS_PER_STATE[stateCode];
  const features = [];

  console.log(`📥 Loading ${districtCount} districts for ${US_STATES[stateCode]}...`);

  // Load each district's GeoJSON file
  for (let i = (stateCode === 'AK' || stateCode === 'DE' || stateCode === 'ND' || stateCode === 'SD' || stateCode === 'VT' || stateCode === 'WY' ? 0 : 1);
       i <= (stateCode === 'AK' || stateCode === 'DE' || stateCode === 'ND' || stateCode === 'SD' || stateCode === 'VT' || stateCode === 'WY' ? 0 : districtCount);
       i++) {
    const districtCode = `${stateCode}-${i}`;
    const filePath = `/geojson/${districtCode}.geojson`;

    try {
      const response = await fetch(filePath);
      if (!response.ok) {
        console.warn(`⚠️ Failed to load ${districtCode}: ${response.status}`);
        continue;
      }

      const geojson = await response.json();

      // Add district metadata to each feature
      // IMPORTANT: Convert Web Mercator coordinates to WGS84 for Leaflet
      if (geojson.type === 'FeatureCollection') {
        // Handle FeatureCollection - add metadata to each feature
        geojson.features.forEach(feature => {
          // Transform coordinates
          if (feature.geometry && feature.geometry.coordinates) {
            feature.geometry.coordinates = webMercatorToWGS84(feature.geometry.coordinates);
          }
          feature.properties = {
            ...feature.properties,
            district_code: districtCode,
            state: stateCode,
            state_name: US_STATES[stateCode]
          };
          features.push(feature);
        });
      } else if (geojson.type === 'Feature') {
        // Handle single Feature - add metadata and transform coordinates
        if (geojson.geometry && geojson.geometry.coordinates) {
          geojson.geometry.coordinates = webMercatorToWGS84(geojson.geometry.coordinates);
        }
        geojson.properties = {
          ...geojson.properties,
          district_code: districtCode,
          state: stateCode,
          state_name: US_STATES[stateCode]
        };
        features.push(geojson);
      } else if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
        // Handle raw geometry - wrap in a Feature object and transform coordinates
        const transformedCoordinates = webMercatorToWGS84(geojson.coordinates);
        const feature = {
          type: 'Feature',
          geometry: {
            type: geojson.type,
            coordinates: transformedCoordinates
          },
          properties: {
            district_code: districtCode,
            state: stateCode,
            state_name: US_STATES[stateCode]
          }
        };
        features.push(feature);
      } else {
        console.warn(`⚠️ Unknown GeoJSON type "${geojson.type}" for ${districtCode}`);
      }
    } catch (error) {
      console.error(`❌ Error loading ${districtCode}:`, error);
    }
  }

  console.log(`✅ Loaded ${features.length} district boundaries for ${US_STATES[stateCode]}`);

  // Enrich features with district data from database
  try {
    const enrichedFeatures = await enrichFeaturesWithDistrictData(features, stateCode);
    return enrichedFeatures;
  } catch (error) {
    console.warn('⚠️ Failed to enrich with district data, returning features without party info:', error);
    return features;
  }
}

/**
 * Enrich GeoJSON features with district data (party, rating, etc.)
 * @param {Array} features - Array of GeoJSON features
 * @param {string} stateCode - Two-letter state code
 * @returns {Promise<Array>} Enriched features
 */
async function enrichFeaturesWithDistrictData(features, stateCode) {
  // Fetch all district data for this state from database
  const { data: districts, error } = await supabase
    .from('districts')
    .select('district_code, incumbent_party, model_rating, margin_2024, is_open_seat')
    .eq('state', stateCode);

  // Create lookup map from database data
  const districtMap = {};
  if (districts && !error) {
    districts.forEach(d => {
      districtMap[d.district_code] = d;

      // WORKAROUND: Database uses AK-AL and DE-AL instead of AK-0 and DE-0
      // Map both versions to the same data until database is updated
      if (d.district_code === 'AK-AL') {
        districtMap['AK-0'] = d;
      } else if (d.district_code === 'DE-AL') {
        districtMap['DE-0'] = d;
      }
    });
  }

  // For California: merge fallback data for any missing districts
  if (stateCode === 'CA') {
    const caDistricts = getCaliforniaDistricts();
    caDistricts.forEach(d => {
      // Only add if not already in database
      if (!districtMap[d.district_code]) {
        districtMap[d.district_code] = {
          district_code: d.district_code,
          incumbent_party: d.incumbent_party,
          model_rating: d.model_rating,
          margin_2024: d.margin_2024,
          is_open_seat: d.is_open_seat
        };
      }
    });
    console.log(`✅ Merged California fallback data (${caDistricts.length} districts)`);
  }

  if (Object.keys(districtMap).length === 0) {
    console.warn(`⚠️ No district data available for ${stateCode}`);
    return features;
  }

  // Enrich each feature with district data
  // IMPORTANT: Create completely new feature objects for React-Leaflet
  const enrichedFeatures = features.map(feature => {
    const districtCode = feature.properties.district_code;
    const districtData = districtMap[districtCode];

    // Create a completely new feature object (deep clone)
    return {
      type: feature.type,
      geometry: feature.geometry,
      properties: {
        ...feature.properties,
        ...(districtData ? {
          incumbent_party: districtData.incumbent_party,
          model_rating: districtData.model_rating,
          margin_2024: districtData.margin_2024,
          is_open_seat: districtData.is_open_seat
        } : {})
      }
    };
  });

  console.log(`✅ Enriched ${enrichedFeatures.length} features with district data`);
  return enrichedFeatures;
}

/**
 * Get user's state based on browser geolocation
 * @returns {Promise<string>} Two-letter state code
 */
export async function getUserState() {
  try {
    // Try IP-based geolocation first (fast)
    const response = await fetch('https://ipapi.co/json/', { timeout: 2000 });
    if (response.ok) {
      const data = await response.json();
      const state = data.region_code;
      if (state && US_STATES[state]) {
        console.log(`Detected user state: ${US_STATES[state]} (${state})`);
        return state;
      }
    }
  } catch (error) {
    console.log('IP geolocation failed, using default state');
  }

  // Default to California if detection fails
  return 'CA';
}

/**
 * Get list of all available states with districts
 * @returns {Array<{code: string, name: string, districts: number}>}
 */
export function getAvailableStates() {
  return Object.entries(US_STATES).map(([code, name]) => ({
    code,
    name,
    districts: DISTRICTS_PER_STATE[code]
  })).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Create a GeoJSON FeatureCollection from loaded features
 * @param {Array} features - Array of GeoJSON features
 * @returns {Object} GeoJSON FeatureCollection
 */
export function createFeatureCollection(features) {
  return {
    type: 'FeatureCollection',
    features: features
  };
}
